// The card of a pirate den (v2-the-deep.md §8.5, R2720, round 118,
// ui-map-interface.md §8 "The NPC side on the card").
//
// A den is no door: its card stands as the peek of the water it lies in, as
// a wreck does. The card wears the NPC ink and the pirate mark. It names the
// den, the design of round 118 and the brotherhood, and its chip states the
// group: home, at sea or sunk. A sunk group adds the time until it sails
// again, counted down on the world clock. The holder of the chart fragment
// of the den reads the pirate cache too, seen or taken, and the prize that
// taken hulls left in it (ruling R-i).

import { Brotherhood, DenCache, DenState } from '../../gen/proto/aquaphobia';
import type { DenInfo, Resources } from '../../gen/proto/aquaphobia';
import { card as S } from '../../gen/strings';
import { costText, dur } from '../../state';
import { denDesign, denLeftSec, type DenSource } from '../../dens';
import { esc } from '../esc';
import type { CardSize, CardSpec, Fact } from './card';
import { markSvg } from './marks';

/** The word of each brotherhood (rounds 110 and 113, R2720): the one table,
 *  which the den card and the pirate card of `mapcards.ts` read. */
export const BROTHERHOOD_WORDS: Readonly<Record<number, string>> = {
  [Brotherhood.BROTHERHOOD_LANTERN_KIN]: S.bro_lantern_kin,
  [Brotherhood.BROTHERHOOD_SALTJAW]: S.bro_saltjaw,
  [Brotherhood.BROTHERHOOD_NINEFATHOM]: S.bro_ninefathom,
  [Brotherhood.BROTHERHOOD_GRAPNEL_COMPANY]: S.bro_grapnel_company,
  [Brotherhood.BROTHERHOOD_UNDERTOW]: S.bro_undertow,
};

/** The word of each den design, by the `dens` name of `[[npc.brotherhoods]]`.
 *  The den card and the den brush of the map designer read it (R3216). */
export const DESIGN_WORDS: Readonly<Record<string, string>> = {
  lantern_wreck: S.ddesign_lantern_wreck,
  broken_ridge: S.ddesign_broken_ridge,
  jaw_cut: S.ddesign_jaw_cut,
  rammed_pair: S.ddesign_rammed_pair,
  sounding_ledge: S.ddesign_sounding_ledge,
  survey_ship: S.ddesign_survey_ship,
  grappled_hauler: S.ddesign_grappled_hauler,
  hook_spur: S.ddesign_hook_spur,
  down_funnel: S.ddesign_down_funnel,
  sunken_dreadnought: S.ddesign_sunken_dreadnought,
};

/** The word of the state of a den's group. */
function denStateWord(state: DenState): string {
  switch (state) {
    case DenState.DEN_STATE_HOME: return S.den_home;
    case DenState.DEN_STATE_AT_SEA: return S.den_at_sea;
    case DenState.DEN_STATE_SUNK: return S.den_sunk;
    default: return S.den_unknown;
  }
}

/** True when a prize holds anything. */
function hasPrize(p: Resources | undefined): p is Resources {
  return !!p && (p.alloy > 0 || p.biomass > 0 || p.volatiles > 0 || p.isotopes > 0 || p.credits > 0);
}

/** The card of a den. `size` is 'peek' for the water peek. */
export function denCard(s: DenSource, d: DenInfo, size: CardSize = 'peek'): CardSpec {
  const design = denDesign(d);
  const facts: Fact[] = [];
  const bro = BROTHERHOOD_WORDS[d.brotherhood];
  if (bro) facts.push({ key: S.f_brotherhood, val: esc(bro) });
  const lines: string[] = [];
  if (d.state === DenState.DEN_STATE_SUNK) {
    const left = denLeftSec(s, d);
    if (left > 0) lines.push(esc(S.den_respawn({ left: dur(left) })));
  }
  // The pirate cache and its prize go to the holder of the fragment alone.
  if (d.cache !== DenCache.DEN_CACHE_NONE) {
    lines.push(esc(d.cache === DenCache.DEN_CACHE_TAKEN ? S.den_cache_taken : S.den_cache_seen));
    if (hasPrize(d.prize)) facts.push({ key: S.den_prize, val: costText(d.prize) });
  }
  return {
    size, mark: markSvg('pirate', 'var(--npc)'), title: S.den_title, ink: { npc: true },
    kind: design ? DESIGN_WORDS[design.key] ?? '' : '',
    status: { text: denStateWord(d.state) },
    close: size === 'card', facts,
    note: lines.length > 0 ? { lines } : undefined,
  };
}
